import type { Event, RSVP } from '@/types';
import { normalizeTicketCoursesToFormatA } from '@/lib/ticket-courses';

export interface CourseSummary {
  name: string;
  options: Array<{ name: string; count: number }>;
}

export type KitchenTicketCourseSummary = {
  ticketId: string;
  ticketName: string;
  courses: Array<{
    courseId: string;
    courseName: string;
    options: Array<{ optionName: string; count: number }>;
  }>;
};

export type TicketQuantitySummary = {
  ticketId: string;
  ticketName: string;
  qty: number;
};

/** Total quantity purchased per ticket type, for multi-tenant events (no course selections to summarise). */
export function buildKitchenTicketQuantitySummary(
  event: Event | null,
  activeRsvps: RSVP[],
): TicketQuantitySummary[] {
  if (!event?.tickets || event.tickets.length === 0) {
    return [];
  }

  const qtyByTicketId = new Map<string, number>();
  activeRsvps.forEach((rsvp) => {
    const lines = Array.isArray(rsvp.responses?.Tickets) ? (rsvp.responses.Tickets as any[]) : [];
    lines.forEach((line) => {
      const qty = Number(line?.qty || 0);
      if (!line?.id || qty <= 0) return;
      qtyByTicketId.set(line.id, (qtyByTicketId.get(line.id) || 0) + qty);
    });
  });

  return event.tickets
    .filter((ticket) => (qtyByTicketId.get(ticket.id) || 0) > 0)
    .map((ticket) => ({
      ticketId: ticket.id,
      ticketName: ticket.name,
      qty: qtyByTicketId.get(ticket.id) || 0,
    }));
}

export type DietaryRequirementEntry = {
  id: string;
  name: string;
  requirement: string;
  tableName: string | null;
  seatNumber: number | null;
};

export type TableAssignmentMap = Record<
  string,
  { roomName: string | null; tableName: string | null; seatNumber: number | null }
>;

/** Allergies responses treated as no real requirement (case insensitive, after trim). */
const PLACEHOLDER_ALLERGY_TEXT = new Set(['none', 'n/a', 'no allergies']);

export function isMeaningfulAllergyResponse(allergies: string): boolean {
  const trimmed = allergies.trim();
  if (!trimmed) {
    return false;
  }
  return !PLACEHOLDER_ALLERGY_TEXT.has(trimmed.toLowerCase());
}

/** Attending diners only: no apologies, not declined/cancelled, not soft-deleted. */
export function getKitchenActiveRsvps(rsvps: RSVP[]): RSVP[] {
  return rsvps.filter(
    (rsvp) =>
      rsvp.deletedAt == null &&
      rsvp.apologies !== true &&
      rsvp.status !== 'declined' &&
      rsvp.status !== 'cancelled',
  );
}

/** Ticket id -> qty from responses.Tickets (only lines with qty > 0). */
export function getRsvpTicketQtyById(rsvp: RSVP): Map<string, number> {
  const map = new Map<string, number>();
  const tickets = rsvp.responses?.Tickets;
  if (!Array.isArray(tickets)) {
    return map;
  }
  tickets.forEach((line) => {
    if (!line?.id) {
      return;
    }
    const qty = Math.floor(Number(line.qty) || 0);
    if (qty > 0) {
      map.set(line.id, qty);
    }
  });
  return map;
}

/**
 * Instance indices to count for kitchen meal totals for one ticket on an RSVP.
 * Aligns with Numbers dining (one row per RSVP): only instances 1..qty from Tickets;
 * skips stale TicketCourses when the ticket line was removed or qty is 0.
 */
export function getKitchenMealInstanceIndices(
  rsvp: RSVP,
  ticketId: string,
  ticketData: Record<number, Record<string, string>>,
): number[] {
  const tickets = rsvp.responses?.Tickets;
  const hasTicketsArray = Array.isArray(tickets) && tickets.length > 0;
  const qtyById = getRsvpTicketQtyById(rsvp);

  const allowedQty = hasTicketsArray ? (qtyById.get(ticketId) ?? 0) : 1;
  if (allowedQty <= 0) {
    return [];
  }

  const indices: number[] = [];
  for (let i = 1; i <= allowedQty; i++) {
    const instanceSelections = ticketData[i];
    if (instanceSelections && typeof instanceSelections === 'object') {
      indices.push(i);
    }
  }
  return indices;
}

export function buildKitchenCourseSummaries(event: Event | null, activeRsvps: RSVP[]): CourseSummary[] {
  if (!event?.courses || event.courses.length === 0) {
    return [];
  }

  return event.courses.map((course) => {
    const options = course.options.map((option) => {
      const count = activeRsvps.reduce((optionCount, rsvp) => {
        const response = rsvp.responses?.[course.name];
        if (typeof response === 'string') {
          return response === option ? optionCount + 1 : optionCount;
        }
        if (Array.isArray(response)) {
          return response.includes(option) ? optionCount + 1 : optionCount;
        }
        return optionCount;
      }, 0);

      return { name: option, count };
    });

    return { name: course.name, options };
  });
}

export function buildKitchenTicketCourseSummaries(
  event: Event | null,
  activeRsvps: RSVP[],
): KitchenTicketCourseSummary[] {
  if (!event?.tickets || event.tickets.length === 0) {
    return [];
  }

  const summaryMap = new Map<
    string,
    {
      ticketId: string;
      ticketName: string;
      courses: Map<
        string,
        {
          courseId: string;
          courseName: string;
          options: Map<string, number>;
        }
      >;
    }
  >();

  event.tickets.forEach((ticket) => {
    if (!ticket?.id || !ticket?.name) {
      return;
    }
    const courseMap = new Map<
      string,
      {
        courseId: string;
        courseName: string;
        options: Map<string, number>;
      }
    >();

    (ticket.courses || []).forEach((course) => {
      if (!course?.id || !course?.name) {
        return;
      }
      const optionMap = new Map<string, number>();
      (course.options || []).forEach((option) => {
        const optionLabel = typeof option === 'string' ? option : String(option ?? '');
        if (optionLabel) {
          optionMap.set(optionLabel, 0);
        }
      });
      courseMap.set(course.id, {
        courseId: course.id,
        courseName: course.name,
        options: optionMap,
      });
    });

    summaryMap.set(ticket.id, {
      ticketId: ticket.id,
      ticketName: ticket.name,
      courses: courseMap,
    });
  });

  const incrementOptionCount = (ticketId: string, courseId: string, optionValue: unknown) => {
    const ticketEntry = summaryMap.get(ticketId);
    if (!ticketEntry) {
      return;
    }
    const courseEntry = ticketEntry.courses.get(courseId);
    if (!courseEntry) {
      return;
    }
    const optionLabelRaw = typeof optionValue === 'string' ? optionValue : String(optionValue ?? '');
    const optionLabel = optionLabelRaw.trim();
    if (!optionLabel) {
      return;
    }
    if (!courseEntry.options.has(optionLabel)) {
      courseEntry.options.set(optionLabel, 0);
    }
    const currentCount = courseEntry.options.get(optionLabel) ?? 0;
    courseEntry.options.set(optionLabel, currentCount + 1);
  };

  activeRsvps.forEach((rsvp) => {
    const normalized = normalizeTicketCoursesToFormatA(rsvp.responses?.TicketCourses);
    Object.entries(normalized).forEach(([ticketId, ticketData]) => {
      if (!summaryMap.has(ticketId) || !ticketData) {
        return;
      }

      const instanceIndices = getKitchenMealInstanceIndices(rsvp, ticketId, ticketData);
      instanceIndices.forEach((instanceIndex) => {
        const instanceSelections = ticketData[instanceIndex];
        if (!instanceSelections || typeof instanceSelections !== 'object') {
          return;
        }
        Object.entries(instanceSelections).forEach(([courseId, optionValue]) => {
          incrementOptionCount(ticketId, courseId, optionValue);
        });
      });
    });
  });

  return Array.from(summaryMap.values())
    .map((ticketEntry) => ({
      ticketId: ticketEntry.ticketId,
      ticketName: ticketEntry.ticketName,
      courses: Array.from(ticketEntry.courses.values())
        .map((courseEntry) => ({
          courseId: courseEntry.courseId,
          courseName: courseEntry.courseName,
          options: Array.from(courseEntry.options.entries()).map(([optionName, count]) => ({
            optionName,
            count,
          })),
        }))
        .filter((course) => course.options.length > 0),
    }))
    .filter((ticket) => ticket.courses.length > 0);
}

export function buildKitchenCombinedCourseSummaries(
  ticketCourseSummaries: KitchenTicketCourseSummary[],
  courseSummaries: CourseSummary[],
): CourseSummary[] {
  if (ticketCourseSummaries.length === 0) {
    return courseSummaries;
  }
  const courseMap = new Map<string, Map<string, number>>();
  ticketCourseSummaries.forEach((ticket) => {
    ticket.courses.forEach((course) => {
      if (!courseMap.has(course.courseName)) {
        courseMap.set(course.courseName, new Map());
      }
      const optionMap = courseMap.get(course.courseName)!;
      course.options.forEach((option) => {
        optionMap.set(option.optionName, (optionMap.get(option.optionName) ?? 0) + option.count);
      });
    });
  });
  return Array.from(courseMap.entries()).map(([name, optionMap]) => ({
    name,
    options: Array.from(optionMap.entries()).map(([optionName, count]) => ({
      name: optionName,
      count,
    })),
  }));
}

export function buildKitchenFallbackMealCounts(
  activeRsvps: RSVP[],
): Array<{ name: string; count: number }> {
  const counts = new Map<string, number>();
  activeRsvps.forEach((rsvp) => {
    const selection = rsvp.responses?.Meal;
    if (typeof selection === 'string' && selection.trim()) {
      const key = selection.trim();
      counts.set(key, (counts.get(key) || 0) + 1);
    }
  });
  return Array.from(counts.entries()).map(([name, count]) => ({ name, count }));
}

export function buildKitchenDietaryRequirements(
  activeRsvps: RSVP[],
  tableAssignments: TableAssignmentMap,
): DietaryRequirementEntry[] {
  const entries: Array<DietaryRequirementEntry | null> = activeRsvps.map((rsvp) => {
    const requirement =
      typeof rsvp.responses?.Allergies === 'string' ? rsvp.responses.Allergies.trim() : '';

    if (!requirement || !isMeaningfulAllergyResponse(requirement)) {
      return null;
    }

    const assignment = tableAssignments[rsvp.id];

    return {
      id: rsvp.id,
      name: rsvp.name,
      requirement,
      tableName: assignment?.tableName ?? null,
      seatNumber: assignment?.seatNumber ?? null,
    } satisfies DietaryRequirementEntry;
  });

  return entries.filter((entry): entry is DietaryRequirementEntry => entry !== null);
}

export function getKitchenNumbersDining(activeRsvps: RSVP[], allRsvps: RSVP[]): number {
  return activeRsvps.length > 0 ? activeRsvps.length : allRsvps.length;
}

/** Sum of option counts for one course group (e.g. all starters). */
export function sumKitchenCourseOptionCounts(course: CourseSummary): number {
  return course.options.reduce((sum, option) => sum + option.count, 0);
}
